import { useState } from "react";
import { toast } from "sonner";
import { Briefcase, Building2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { IMAGES } from "@/lib/constants";
import { api } from "@/lib/api";

const FEATURES = ["Employee travel", "Airport transfers", "Client transportation", "Corporate accounts", "Monthly billing", "Dedicated support", "Multiple passenger bookings", "Central billing"];

export default function Corporate() {
  const [form, setForm] = useState({ company: "", contact: "", email: "", phone: "", size: "", monthly_trips: "", requirements: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company || !form.email) { toast.error("Please provide company name and email"); return; }
    setLoading(true);
    try { await api.post("/corporate-enquiries", form); setDone(true); toast.success("Enquiry submitted — our team will be in touch."); }
    catch { toast.error("Could not submit enquiry"); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <section className="relative bg-navy">
        <img src={IMAGES.mercedes} alt="Corporate" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-navy/70" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14 lg:py-20 text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white"><Briefcase size={14} className="text-brand" /> Corporate Travel</span>
          <h1 className="mt-4 font-heading text-4xl font-bold text-white lg:text-5xl">Reliable Travel for Your Business</h1>
        </div>
      </section>
      <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-16 grid gap-10 lg:grid-cols-2">
        <div>
          <h2 className="font-heading text-2xl font-bold text-navy">Everything your team needs</h2>
          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            {FEATURES.map((f) => <div key={f} className="flex items-center gap-2 rounded-xl border border-slate-200 p-3 text-sm text-slate-700"><CheckCircle2 size={16} className="text-emerald-500" /> {f}</div>)}
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          {done ? (
            <div className="py-16 text-center"><CheckCircle2 size={48} className="mx-auto text-emerald-500" /><h3 className="mt-4 font-heading text-lg font-semibold text-navy">Enquiry received</h3><p className="mt-2 text-sm text-slate-500">Our corporate team will contact you shortly.</p></div>
          ) : (
            <form onSubmit={submit} className="grid gap-3">
              <h3 className="flex items-center gap-2 font-heading text-lg font-semibold text-navy"><Building2 size={18} className="text-brand" /> Create Corporate Account</h3>
              <div><Label>Company Name</Label><Input value={form.company} onChange={set("company")} data-testid="corp-company" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Contact Person</Label><Input value={form.contact} onChange={set("contact")} data-testid="corp-contact" /></div>
                <div><Label>Phone</Label><Input value={form.phone} onChange={set("phone")} data-testid="corp-phone" /></div>
              </div>
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={set("email")} data-testid="corp-email" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Company Size</Label><Input value={form.size} onChange={set("size")} data-testid="corp-size" placeholder="e.g. 50-200" /></div>
                <div><Label>Monthly Trips</Label><Input value={form.monthly_trips} onChange={set("monthly_trips")} data-testid="corp-trips" placeholder="e.g. 100" /></div>
              </div>
              <div><Label>Travel Requirements</Label><Input value={form.requirements} onChange={set("requirements")} data-testid="corp-req" /></div>
              <div><Label>Message</Label><Textarea value={form.message} onChange={set("message")} data-testid="corp-message" /></div>
              <Button type="submit" className="bg-brand hover:bg-brand/90" disabled={loading} data-testid="corp-submit">{loading ? "Submitting…" : "Submit Enquiry"}</Button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
